import React from 'react';
import type { BookingClassificationResponse, DgVerdict, BookingField } from '../services/api';
import ValidateEntries from './ValidateEntries';

interface BookingResultProps {
  result: BookingClassificationResponse;
}

function percent(probability: number): string {
  return `${(probability * 100).toFixed(0)}%`;
}

const VerdictBadge: React.FC<{ verdict: DgVerdict }> = ({ verdict }) => (
  <span className={`tag tag-verdict ${verdict.prediction ? 'dangerous' : 'safe'}`}>
    {verdict.prediction ? 'Dangerous' : 'Safe'}
  </span>
);

const VerdictNotes: React.FC<{ verdict: DgVerdict }> = ({ verdict }) => {
  if (!verdict.unRuleDg && verdict.scored) return null;

  return (
    <div className="verdict-notes">
      {verdict.unRuleDg && (
        <div>Declared UN number with no exempting special provision: verdict forced to dangerous.</div>
      )}
      {!verdict.scored && (
        <div>Nothing in the fields could be scored; the probability is the customer prior.</div>
      )}
    </div>
  );
};

// The fields DGML was sent, each with its own probability where the model gave one
const FieldTable: React.FC<{ fields: BookingField[]; verdict: DgVerdict }> = ({ fields, verdict }) => (
  <table className="booking-fields">
    <tbody>
      {fields.map((field, index) => {
        const scored = verdict.fieldResults[index];
        return (
          <tr key={index}>
            <td className="booking-field-name">{field.field}</td>
            <td className="booking-field-text">{field.text}</td>
            <td className="booking-field-score">
              {scored && scored.scored ? percent(scored.probability) : '—'}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const BookingResult: React.FC<BookingResultProps> = ({ result }) => {
  const flaggedCargo = result.cargo.filter((item) => item.prediction && !item.declaredDg).length;

  return (
    <div className="card result-card">
      <div className="result-header">
        <span className="h5">Booking {result.reference ?? ''}</span>
        <span className="result-tags">
          <span className="tag">dg {result.modelVersion}</span>
          {result.customer && <span className="tag">{result.customer}</span>}
        </span>
      </div>

      <div className="result-note">
        {result.cargo.length} cargo item{result.cargo.length === 1 ? '' : 's'}
        {result.declarations.hasDg && ', some declared dangerous'}
        {!result.declarations.hasNonDg && result.cargo.length > 0 && ' (all declared: the live job would not score this booking)'}
        {flaggedCargo > 0 && `; ${flaggedCargo} undeclared item${flaggedCargo === 1 ? '' : 's'} predicted dangerous`}
      </div>

      {result.cargo.map((item) => (
        <div key={item.id} className={`booking-cargo ${item.prediction ? 'dangerous' : 'safe'}`}>
          <div className="booking-cargo-header">
            <span className="booking-cargo-title">
              CTU {item.ctuIndex + 1}{item.ctuId ? ` (${item.ctuId})` : ''} · cargo {item.cargoIndex + 1}
              {item.declaredDg && <span className="tag tag-declared">declared DG</span>}
            </span>
            <span className="result-tags">
              <VerdictBadge verdict={item} />
              <span className="tag tag-score">{percent(item.probability)}</span>
            </span>
          </div>

          <FieldTable fields={item.fields} verdict={item} />
          <VerdictNotes verdict={item} />

          {item.unNumbers && item.unNumbers.length > 0 && (
            <div className="booking-un-numbers">
              {item.unNumbers.map((un) => (
                <div key={un.prediction} className="booking-un-number">
                  <span>
                    <strong>{un.prediction.toUpperCase()}</strong> {percent(un.probability)}
                  </span>
                  <ValidateEntries lookup={un.validate} compact />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {result.booking && (
        <div className={`booking-cargo booking-level ${result.booking.prediction ? 'dangerous' : 'safe'}`}>
          <div className="booking-cargo-header">
            <span className="booking-cargo-title">Booking-level text</span>
            <span className="result-tags">
              <VerdictBadge verdict={result.booking} />
              <span className="tag tag-score">{percent(result.booking.probability)}</span>
            </span>
          </div>

          <FieldTable fields={result.booking.fields} verdict={result.booking} />
          <VerdictNotes verdict={result.booking} />
        </div>
      )}

      <div className="result-note" style={{ marginTop: '0.75rem' }}>
        Fields are what the live prediction job sends: cargo items go through the DG model and then the UN model
        for anything predicted dangerous; top-level text is scored on its own.
      </div>
    </div>
  );
};

export default BookingResult;
